/**
 * Light and dark palettes, and the media-query plumbing that picks one.
 *
 * VOSviewer is a white-background desktop application with a black-background
 * option; a web map has to sit inside whatever the page is wearing.  The two
 * palettes below keep VOSviewer's actual hues — the cluster colours, the
 * viridis ramp, the grey for "no value" — and only move the surfaces around
 * them, so a map screenshotted from either theme still reads as the same map.
 *
 * The density views are the exception: their field is drawn *from* the
 * background outwards, so both themes give them a dark ground.  That is
 * VOSviewer's default too (`USE_WHITE_BACKGROUND_CLUSTER_DENSITY_VIEW = false`),
 * and a viridis ramp over white simply does not work.
 */

import type { Theme, ThemePreference } from '../types';

export const LIGHT_THEME: Theme = {
  name: 'light',
  background: [255, 255, 255],
  densityBackground: [0, 0, 0],
  label: [0, 0, 0],
  mutedLabel: [110, 110, 118],
  defaultLine: [191, 191, 191],
  noValue: [191, 191, 191],
  focusRing: [0, 95, 204],
  legendText: [40, 40, 46],
  legendFrame: [190, 190, 196],
  tooltipBackground: [255, 255, 255],
  tooltipText: [24, 24, 28],
  tooltipBorder: [200, 200, 208],
};

export const DARK_THEME: Theme = {
  name: 'dark',
  background: [17, 18, 22],
  densityBackground: [0, 0, 0],
  label: [236, 237, 242],
  mutedLabel: [150, 152, 162],
  defaultLine: [110, 112, 122],
  noValue: [128, 128, 134],
  focusRing: [110, 168, 254],
  legendText: [226, 227, 234],
  legendFrame: [78, 80, 90],
  tooltipBackground: [30, 31, 38],
  tooltipText: [236, 237, 242],
  tooltipBorder: [70, 72, 82],
};

const DARK_QUERY = '(prefers-color-scheme: dark)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function matchMediaSafe(query: string): MediaQueryList | null {
  // Guarded because the module is also loaded in Node for unit tests and in
  // workers, where `window` is absent.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(query);
}

export function prefersDark(): boolean {
  return matchMediaSafe(DARK_QUERY)?.matches ?? false;
}

export function prefersReducedMotion(): boolean {
  return matchMediaSafe(REDUCED_MOTION_QUERY)?.matches ?? false;
}

export function resolveTheme(preference: ThemePreference): Theme {
  if (preference === 'light') return LIGHT_THEME;
  if (preference === 'dark') return DARK_THEME;
  return prefersDark() ? DARK_THEME : LIGHT_THEME;
}

/** Subscribe to a media query. Returns an unsubscribe function; never throws. */
function watchQuery(query: string, onChange: () => void): () => void {
  const mql = matchMediaSafe(query);
  if (!mql) return () => undefined;
  const handler = (): void => onChange();
  // Safari before 14 only has the deprecated addListener; the cast keeps that
  // path compiling without widening the whole MediaQueryList type.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }
  const legacy = mql as unknown as {
    addListener(cb: () => void): void;
    removeListener(cb: () => void): void;
  };
  legacy.addListener(handler);
  return () => legacy.removeListener(handler);
}

export function watchColorScheme(onChange: () => void): () => void {
  return watchQuery(DARK_QUERY, onChange);
}

export function watchReducedMotion(onChange: () => void): () => void {
  return watchQuery(REDUCED_MOTION_QUERY, onChange);
}
